<template>
  <main class="marketing-page">
    <section class="marketing-shell">
      <header class="marketing-header">
        <p class="marketing-eyebrow">Marketing</p>
        <h1>Digital Marketing</h1>
        <p class="marketing-subtitle">
          See which channels bring in leads, and how many of them turn into won jobs.
        </p>
      </header>

      <div v-if="loadError" class="banner banner-error">{{ loadError }}</div>

      <section class="panel">
        <div class="panel-header">
          <h2>Lead Sources</h2>
          <p>Based on the source recorded on each lead in your CRM.</p>
        </div>

        <div v-if="loading" class="empty-state">
          <v-progress-circular indeterminate size="24" color="primary" />
          <span>Loading lead sources…</span>
        </div>

        <template v-else-if="sources.length">
          <div class="stat-row">
            <div class="stat-tile">
              <span class="stat-value">{{ totalLeads }}</span>
              <span class="stat-label">Total leads</span>
            </div>
            <div class="stat-tile">
              <span class="stat-value">{{ totalWon }}</span>
              <span class="stat-label">Won</span>
            </div>
            <div class="stat-tile">
              <span class="stat-value">{{ overallWinRate }}%</span>
              <span class="stat-label">Win rate</span>
            </div>
          </div>

          <BarChart :labels="sourceLabelsList" :datasets="chartDatasets" :height="260" />

          <table class="source-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Leads</th>
                <th>Won</th>
                <th>Win rate</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in sources" :key="row.source">
                <td>{{ sourceLabel(row.source) }}</td>
                <td>{{ row.total }}</td>
                <td>{{ row.won }}</td>
                <td>{{ winRate(row) }}%</td>
              </tr>
            </tbody>
          </table>
        </template>

        <div v-else class="empty-state">
          <v-icon size="28">mdi-chart-bar</v-icon>
          <span>No leads yet — this fills in as leads come in.</span>
        </div>
      </section>

      <section class="panel">
        <div class="panel-header">
          <h2>Ad Accounts</h2>
          <p>Connect your Google Ads and Meta Ads accounts to see spend alongside the leads they produce.</p>
        </div>

        <div v-if="adAccountsNotice" class="ad-accounts-notice">{{ adAccountsNotice }}</div>

        <div v-if="connectedAccounts.length" class="connected-accounts">
          <div v-for="acct in connectedAccounts" :key="acct.id" class="connected-account-row">
            <v-icon size="20">{{ acct.provider === 'google' ? 'mdi-google' : 'mdi-facebook' }}</v-icon>
            <span class="connected-account-name">{{ acct.display_name || acct.external_account_id }}</span>
            <span class="connected-account-status" :class="`status-${acct.status}`">{{ acct.status }}</span>
            <button class="row-action" type="button" @click="disconnectAccount(acct)">Disconnect</button>
          </div>
        </div>

        <div class="ad-accounts-grid">
          <button
            class="ad-account-card"
            type="button"
            :disabled="connecting === 'google'"
            @click="connectAccount('google')"
          >
            <v-icon size="28">mdi-google</v-icon>
            <div>
              <strong>Google Ads</strong>
              <span>Connect account</span>
            </div>
          </button>
          <button
            class="ad-account-card"
            type="button"
            :disabled="connecting === 'meta'"
            @click="connectAccount('meta')"
          >
            <v-icon size="28">mdi-facebook</v-icon>
            <div>
              <strong>Meta Ads</strong>
              <span>Connect account</span>
            </div>
          </button>
        </div>
      </section>
    </section>
  </main>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'
import { company, getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { sourceLabels } from '@/constants/leads'
import BarChart from '@/components/marketing/BarChart.vue'

const sources = ref([])
const loading = ref(false)
const loadError = ref('')

const connectedAccounts = ref([])
const connecting = ref(null)
const adAccountsNotice = ref('')

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function fetchConnectedAccounts() {
  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/marketing/ad-accounts?company_id=${encodeURIComponent(company.company_id)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))
    if (response.ok && data.ok) {
      connectedAccounts.value = data.accounts || []
    }
  } catch {
    // Non-critical for this skeleton — an empty list is a fine fallback.
  }
}

watchEffect(() => {
  if (company.company_id) fetchConnectedAccounts()
})

// Phase 5 is still a skeleton (see backend/routes/marketingAds.js) — real
// OAuth needs an approved app with each provider first. This surfaces that
// clearly instead of pretending the button works.
async function connectAccount(provider) {
  connecting.value = provider
  adAccountsNotice.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/marketing/oauth/${provider}/start?company_id=${encodeURIComponent(company.company_id)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))
    adAccountsNotice.value = data.error || 'Connecting an ad account isn\'t available yet.'
  } catch {
    adAccountsNotice.value = 'Connecting an ad account isn\'t available yet.'
  } finally {
    connecting.value = null
  }
}

async function disconnectAccount(account) {
  if (!confirm(`Disconnect ${account.display_name || account.external_account_id}?`)) return

  try {
    const headers = await authHeaders()
    await fetch(`${API_BASE_URL}/marketing/ad-accounts/${account.id}`, { method: 'DELETE', headers })
    await fetchConnectedAccounts()
  } catch (err) {
    adAccountsNotice.value = err.message || 'Failed to disconnect account.'
  }
}

async function fetchLeadSources() {
  loading.value = true
  loadError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/marketing/lead-sources?company_id=${encodeURIComponent(company.company_id)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to load lead sources.')
    }

    sources.value = data.sources || []
  } catch (err) {
    loadError.value = err.message || 'Failed to load lead sources.'
  } finally {
    loading.value = false
  }
}

watchEffect(() => {
  if (company.company_id) fetchLeadSources()
})

function sourceLabel(source) {
  return sourceLabels[source] || source
}

function winRate(row) {
  return row.total ? Math.round((row.won / row.total) * 100) : 0
}

const totalLeads = computed(() => sources.value.reduce((sum, row) => sum + row.total, 0))
const totalWon = computed(() => sources.value.reduce((sum, row) => sum + row.won, 0))
const overallWinRate = computed(() =>
  totalLeads.value ? Math.round((totalWon.value / totalLeads.value) * 100) : 0
)

const sourceLabelsList = computed(() => sources.value.map((row) => sourceLabel(row.source)))
const chartDatasets = computed(() => [
  { label: 'Total leads', data: sources.value.map((row) => row.total), backgroundColor: '#f26e2e' },
  { label: 'Won', data: sources.value.map((row) => row.won), backgroundColor: '#1f5c4d' },
])
</script>

<style scoped>
.marketing-page {
  min-height: calc(100dvh - 70px);
  padding: 32px 18px 110px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.marketing-shell {
  width: min(980px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 20px;
}

.marketing-eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.marketing-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 850;
  line-height: 1.1;
}

.marketing-subtitle {
  margin: 8px 0 0;
  color: var(--text-secondary);
  font-size: 0.92rem;
}

.banner {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.panel {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 20px;
}

.panel-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
}

.panel-header p {
  margin: 4px 0 16px;
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 16px;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.stat-tile {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
}

.stat-value {
  font-size: 1.4rem;
  font-weight: 850;
}

.stat-label {
  color: var(--text-secondary);
  font-size: 0.78rem;
}

.source-table {
  width: 100%;
  margin-top: 20px;
  border-collapse: collapse;
  font-size: 0.86rem;
}

.source-table th {
  text-align: left;
  padding: 8px 10px;
  color: var(--text-secondary);
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid var(--border-subtle);
}

.source-table td {
  padding: 10px;
  border-bottom: 1px solid var(--border-subtle);
}

.ad-accounts-notice {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  color: #8a6d1f;
  background: #fff1dc;
  border: 1px solid #f0e2b8;
  font-size: 0.84rem;
}

.connected-accounts {
  display: grid;
  gap: 8px;
  margin-bottom: 14px;
}

.connected-account-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
}

.connected-account-name {
  flex: 1;
  font-size: 0.86rem;
  font-weight: 700;
}

.connected-account-status {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.status-active { color: #1f5c4d; background: #eef8f5; }
.status-disconnected { color: var(--text-secondary); background: var(--surface); }
.status-error { color: #8a2b1f; background: #fdecea; }

.row-action {
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}

.ad-accounts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.ad-account-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.ad-account-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ad-account-card strong {
  display: block;
  font-size: 0.92rem;
  color: var(--text-primary);
}

.ad-account-card span {
  display: block;
  margin-top: 2px;
  color: var(--text-secondary);
  font-size: 0.78rem;
}

@media (max-width: 620px) {
  .marketing-page {
    padding: 22px 14px 110px;
  }

  .stat-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
